import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (data) {
      setProfile(data);
    }
    setLoading(false);
  };

  const updateProfile = async (updates) => {
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (data) setProfile(data);
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const logAction = async (action) => {
    if (!user) return;
    await supabase.from('logs').insert({ user_id: user.id, action, ip_address: null });

    // Suspicious activity detection: check for rapid deletions or downloads
    if (action.startsWith('Deleted file:') || action.startsWith('Downloaded file:')) {
      const since = new Date(Date.now() - 60000).toISOString();
      const keyword = action.startsWith('Deleted') ? 'Deleted file:' : 'Downloaded file:';
      const { count } = await supabase.from('logs').select('*', { count: 'exact', head: true })
        .eq('user_id', user.id).gte('created_at', since).like('action', `${keyword}%`);
      if (count >= 3) {
        await supabase.from('suspicious_events').insert({
          user_id: user.id,
          event_type: action.startsWith('Deleted') ? 'Rapid File Deletion' : 'Bulk File Download',
          description: `User performed ${count} ${action.startsWith('Deleted') ? 'deletions' : 'downloads'} within 60 seconds`,
          severity: count >= 5 ? 'critical' : 'high',
        });
      }
    }
  };

  const flagSuspicious = async (eventType, description, severity = 'medium') => {
    if (!user) return;
    await supabase.from('suspicious_events').insert({ user_id: user.id, event_type: eventType, description, severity });
  };

  return (
    <AuthContext.Provider value={{
      user, profile, loading, setProfile,
      signOut, updateProfile, logAction, fetchProfile, flagSuspicious
    }}>
      {children}
    </AuthContext.Provider>
  );
};
